import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { IconeSeta } from './Icones.js';
import './Campo.css';

/**
 * Campos de formulário do painel.
 *
 * Rótulo sempre visível e ligado ao controle: placeholder não é rótulo — some
 * assim que a pessoa digita, e some de vez para o leitor de tela. O erro do
 * campo é ligado por `aria-describedby`, então quem usa leitor ouve o motivo
 * ao chegar no campo, e não só ao varrer a tela.
 */

type BaseProps = {
  readonly rotulo: string;
  readonly obrigatorio?: boolean;
  /** Texto de apoio. Some quando há erro — dois textos brigando confundem. */
  readonly ajuda?: string;
  /** Mensagem já traduzida (ver `api/erros.ts`). */
  readonly erro?: string;
};

function Envelope({
  rotulo,
  obrigatorio,
  ajuda,
  erro,
  idControle,
  idDescricao,
  children,
}: BaseProps & {
  readonly idControle: string;
  readonly idDescricao: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="campo">
      <label className="campo__rotulo" htmlFor={idControle}>
        {rotulo}
        {obrigatorio === true && (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {erro !== undefined ? (
        <p className="campo__erro" id={idDescricao}>
          {erro}
        </p>
      ) : (
        ajuda !== undefined && (
          <p className="campo__ajuda" id={idDescricao}>
            {ajuda}
          </p>
        )
      )}
    </div>
  );
}

type PropsTexto = BaseProps & {
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly tipo?: 'text' | 'password' | 'number' | 'search' | 'email';
  readonly placeholder?: string;
  /** `mono` para o que se lê caractere a caractere: identificador, depósito, SKU. */
  readonly fonte?: 'base' | 'mono';
  readonly autoCompletar?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  readonly desabilitado?: boolean;
  readonly maximoDeCaracteres?: number;
};

export function CampoTexto({
  valor,
  aoMudar,
  tipo = 'text',
  placeholder,
  fonte = 'base',
  autoCompletar,
  desabilitado = false,
  maximoDeCaracteres,
  ...base
}: PropsTexto) {
  const idControle = useId();
  const idDescricao = useId();
  const temDescricao = base.erro !== undefined || base.ajuda !== undefined;

  return (
    <Envelope {...base} idControle={idControle} idDescricao={idDescricao}>
      <input
        id={idControle}
        className={`campo__controle campo__controle--${fonte}`}
        type={tipo}
        value={valor}
        placeholder={placeholder}
        required={base.obrigatorio === true}
        disabled={desabilitado}
        maxLength={maximoDeCaracteres}
        autoComplete={autoCompletar}
        aria-invalid={base.erro !== undefined ? true : undefined}
        aria-describedby={temDescricao ? idDescricao : undefined}
        onChange={(evento: ChangeEvent<HTMLInputElement>) => {
          aoMudar(evento.target.value);
        }}
      />
    </Envelope>
  );
}

type PropsArea = BaseProps & {
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly maximoDeCaracteres?: number;
  readonly linhas?: number;
  readonly desabilitado?: boolean;
};

/**
 * Texto livre de várias linhas — motivo, justificativa. O limite vai no
 * próprio controle: o navegador para de aceitar no teto, em vez de a pessoa
 * descobrir o limite só pela recusa do servidor.
 */
export function CampoAreaTexto({
  valor,
  aoMudar,
  maximoDeCaracteres,
  linhas = 3,
  desabilitado = false,
  ...base
}: PropsArea) {
  const idControle = useId();
  const idDescricao = useId();
  const temDescricao = base.erro !== undefined || base.ajuda !== undefined;

  return (
    <Envelope {...base} idControle={idControle} idDescricao={idDescricao}>
      <textarea
        id={idControle}
        className="campo__controle campo__controle--area"
        value={valor}
        rows={linhas}
        required={base.obrigatorio === true}
        disabled={desabilitado}
        maxLength={maximoDeCaracteres}
        aria-invalid={base.erro !== undefined ? true : undefined}
        aria-describedby={temDescricao ? idDescricao : undefined}
        onChange={(evento: ChangeEvent<HTMLTextAreaElement>) => {
          aoMudar(evento.target.value);
        }}
      />
    </Envelope>
  );
}

export type OpcaoDeCampo = {
  readonly valor: string;
  readonly rotulo: string;
  /** Opção que existe mas não pode ser escolhida agora — quem chama explica o porquê. */
  readonly desabilitada?: boolean;
};

type PropsSelecao = BaseProps & {
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly opcoes: readonly OpcaoDeCampo[];
  readonly desabilitado?: boolean;
};

/**
 * Seleção. É um `<select>` de verdade: teclado, leitor de tela e o seletor
 * nativo do sistema já sabem operá-lo — nada disso se reescreve de graça.
 */
export function CampoSelecao({
  valor,
  aoMudar,
  opcoes,
  desabilitado = false,
  ...base
}: PropsSelecao) {
  const idControle = useId();
  const idDescricao = useId();
  const temDescricao = base.erro !== undefined || base.ajuda !== undefined;

  return (
    <Envelope {...base} idControle={idControle} idDescricao={idDescricao}>
      <span className="campo__caixa-selecao">
        <select
          id={idControle}
          className="campo__controle campo__controle--selecao"
          value={valor}
          required={base.obrigatorio === true}
          disabled={desabilitado}
          aria-invalid={base.erro !== undefined ? true : undefined}
          aria-describedby={temDescricao ? idDescricao : undefined}
          onChange={(evento: ChangeEvent<HTMLSelectElement>) => {
            aoMudar(evento.target.value);
          }}
        >
          {opcoes.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor} disabled={opcao.desabilitada === true}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
        <span className="campo__seta">
          <IconeSeta tamanho={13} />
        </span>
      </span>
    </Envelope>
  );
}
